import React, { useState, useEffect, useRef, useContext } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Container,
  Row,
  Col,
  Form,
  Button,
  Card,
  Alert,
  Spinner,
  ListGroup,
  Badge,
  Modal,
  Toast,
  ToastContainer,
  Dropdown,
} from "react-bootstrap";
import { useReactToPrint } from "react-to-print";
import { FaCog, FaPrint } from "react-icons/fa";
import { AuthContext } from "../contexts/AuthContext";
import { toast, Toaster } from "react-hot-toast";
import api from "../api";
import SmartPDFButton from "./SmartPDFButton";
import SmartPDFSettings from "./SmartPDFSettings";

const WorksheetGenerator = () => {
  // Add toast container at the beginning of the component

  // Get router location to check for worksheet ID
  const location = useLocation();
  const navigate = useNavigate();

  // State for user input phase
  const [subject, setSubject] = useState("");
  const [topic, setTopic] = useState("");
  const [topicSuggestions, setTopicSuggestions] = useState([]);
  const [showTopicSuggestions, setShowTopicSuggestions] = useState(false);
  const [difficulty, setDifficulty] = useState("");
  const [numQuestions, setNumQuestions] = useState(5);
  const [paper, setPaper] = useState("");
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState("");
  const [subjects, setSubjects] = useState([]);

  // State for worksheet questions
  const [generatedQuestions, setGeneratedQuestions] = useState([]);
  const [selectedQuestions, setSelectedQuestions] = useState([]);

  // State for regeneration phase
  const [regenerateTopic, setRegenerateTopic] = useState("");
  const [regenerateDifficulty, setRegenerateDifficulty] = useState("");
  const [regeneratePaper, setRegeneratePaper] = useState("");
  const [isRegenerating, setIsRegenerating] = useState(false);

  // State for preview mode
  const [showPreview, setShowPreview] = useState(false);
  const printRef = useRef();

  // Print status tracking
  const [printStatus, setPrintStatus] = useState(null); // null, 'preparing', 'printing', 'success', 'error'
  const [printError, setPrintError] = useState("");
  const [showPrintStatus, setShowPrintStatus] = useState(false);

  // Worksheet saving and loading
  const [savedWorksheets, setSavedWorksheets] = useState([]);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [showLoadModal, setShowLoadModal] = useState(false);
  const [isLoadingWorksheets, setIsLoadingWorksheets] = useState(false);
  const [isSavingWorksheet, setIsSavingWorksheet] = useState(false);
  const [worksheetTitle, setWorksheetTitle] = useState("");
  const [worksheetDescription, setWorksheetDescription] = useState("");
  const [isLoadingQuestions, setIsLoadingQuestions] = useState(false);
  const [isViewOnly, setIsViewOnly] = useState(false);

  // Add levels state for dynamic difficulty options
  const [levels, setLevels] = useState([]);

  // Auth context for user info
  const { auth } = useContext(AuthContext);

  // My Questions integration
  const [includeMyQuestions, setIncludeMyQuestions] = useState(false);

  // Smart PDF settings
  const [smartPDFSettings, setSmartPDFSettings] = useState({
    question_spacing: 0.5,
    page_threshold: 80,
    answer_spacing: 0.5,
    answer_page_threshold: 80,
    enable_continuation_headers: true,
    show_source_badges: true,
  });
  const [showSettingsModal, setShowSettingsModal] = useState(false);

  // Set default difficulty values once levels are loaded
  useEffect(() => {
    if (levels.length > 0 && !difficulty) {
      // Set default difficulty to the first option if not already set
      setDifficulty(levels[0]);
    }
    if (levels.length > 0 && !regenerateDifficulty) {
      // Set default regenerate difficulty to the first option if not already set
      setRegenerateDifficulty(levels[0]);
    }
  }, [levels, difficulty, regenerateDifficulty]);

  // Load Smart PDF settings from localStorage
  useEffect(() => {
    const savedSettings = localStorage.getItem("lioncity_smart_pdf_settings");
    if (savedSettings) {
      try {
        const parsed = JSON.parse(savedSettings);
        setSmartPDFSettings((prev) => ({ ...prev, ...parsed }));
      } catch (error) {
        console.error("Error loading Smart PDF settings:", error);
      }
    }
  }, []);

  // Fetch available subjects when component mounts
  useEffect(() => {
    fetchSubjects();
    fetchLevels(); // Add fetchLevels to be called when component mounts
    fetchSavedWorksheets();

    // Check if we're loading a specific worksheet from My Worksheets page
    const worksheetId = location.state?.worksheetId;
    if (worksheetId) {
      loadWorksheetById(worksheetId);
    }
    // Check if we have questions passed from MyQuestions component
    else if (
      location.state?.selectedQuestions &&
      location.state.fromMyQuestions
    ) {
      loadQuestionsFromMyQuestions(location.state.selectedQuestions);
    }
  }, [location.state]);

  const fetchSubjects = async () => {
    try {
      setIsLoadingWorksheets(true);
      const response = await api.get("/api/csv-data/subjects/");
      if (response.data && Array.isArray(response.data)) {
        setSubjects(response.data);
      }
    } catch (error) {
      console.error("Error fetching subjects:", error);
      setError("Failed to fetch subjects. Please try again later.");
    } finally {
      setIsLoadingWorksheets(false);
    }
  };

  // Add fetchLevels function to get dynamic level options from API
  const fetchLevels = async () => {
    try {
      const response = await api.get("/api/csv-data/levels/");
      if (response.data && Array.isArray(response.data)) {
        console.log("Available levels:", response.data);
        setLevels(response.data);
      }
    } catch (err) {
      console.error("Error fetching levels:", err);
      // If the levels endpoint is not available, extract levels from data
      try {
        console.log("Levels endpoint not available, extracting from data");
        const dataResponse = await api.get("/api/csv-data/?limit=1000");
        if (dataResponse.data && dataResponse.data.results) {
          // Extract unique levels from data
          const uniqueLevels = [
            ...new Set(
              dataResponse.data.results
                .map((item) => item.level)
                .filter((level) => level && level.trim() !== "")
            ),
          ].sort();

          console.log("Extracted levels:", uniqueLevels);
          setLevels(uniqueLevels);
        }
      } catch (extractErr) {
        console.error("Error extracting levels from data:", extractErr);
        // Set some default levels as fallback
        setLevels(["Basic", "Intermediate", "Hard"]);
      }
    }
  };

  const loadWorksheetById = async (worksheetId) => {
    setIsLoadingQuestions(true);
    setError("");

    try {
      // Check if we're loading a public worksheet
      const isPublic = location.state?.isPublic;
      console.log(
        "Loading worksheet with ID:",
        worksheetId,
        "Public:",
        isPublic
      );

      let endpoint;
      if (isPublic) {
        // For public worksheets, we need to use a different endpoint
        endpoint = `/api/worksheets/${worksheetId}/questions/`;
      } else {
        // For your own worksheets
        endpoint = `/api/worksheets/${worksheetId}/questions/`;
      }

      const response = await api.get(endpoint);

      if (response.data) {
        const { worksheet, questions } = response.data;

        console.log("Loaded worksheet:", worksheet);
        console.log("Questions:", questions);

        // Set worksheet parameters
        setSubject(worksheet.subject);
        setTopic(worksheet.topic || "");

        // Check if worksheet.difficulty exists in the current levels array
        // If not, we'll still set it but will rely on the levels useEffect to handle defaults
        setDifficulty(worksheet.difficulty || "");

        // Set My Questions inclusion flag if it exists in the worksheet data
        if (worksheet.include_my_questions !== undefined) {
          setIncludeMyQuestions(worksheet.include_my_questions);
        }

        // Try to parse question_sources if available
        let questionSources = {};
        try {
          if (worksheet.question_sources) {
            const sources = JSON.parse(worksheet.question_sources);
            questionSources = sources.reduce((acc, item) => {
              acc[item.reference_code] = item.source;
              return acc;
            }, {});
          }
        } catch (e) {
          console.error("Error parsing question sources:", e);
        }

        // Process questions with tempId for the UI
        const questionsWithId = questions.map((q, index) => ({
          ...q,
          tempId: `saved-${index}`, // Add temporary ID for tracking
          selected: true, // All questions start as selected
          // Apply source if available in question_sources, otherwise use default from question or "system"
          source: questionSources[q.reference_code] || q.source || "system",
        }));

        setGeneratedQuestions(questionsWithId);
        setSelectedQuestions(questionsWithId.map((q) => q.tempId));

        // Pre-populate the save form with the worksheet info
        setWorksheetTitle(worksheet.title);
        setWorksheetDescription(worksheet.description || "");

        // If it's a public worksheet created by someone else, we should mark it as view-only
        const isOwnedByCurrentUser = worksheet.user_id === auth.user?.id;
        setIsViewOnly(isPublic && !isOwnedByCurrentUser);

        if (isPublic && !isOwnedByCurrentUser) {
          toast.info(
            "You are viewing a shared worksheet. You can use it but not modify or save changes."
          );
        }
      }
    } catch (error) {
      console.error("Error loading worksheet:", error);
      setError("Failed to load the worksheet. Please try again.");
    } finally {
      setIsLoadingQuestions(false);
    }
  };

  // Fetch saved worksheets
  const fetchSavedWorksheets = async () => {
    if (!auth.user) return;

    setIsLoadingWorksheets(true);
    try {
      const response = await api.get("/api/worksheets/");
      if (response.data && Array.isArray(response.data)) {
        setSavedWorksheets(response.data);
      }
    } catch (error) {
      console.error("Error fetching saved worksheets:", error);
      setError("Failed to fetch saved worksheets. Please try again later.");
    } finally {
      setIsLoadingWorksheets(false);
    }
  };

  // Load a saved worksheet
  const loadWorksheet = async (worksheetId) => {
    setIsLoadingQuestions(true);
    setError("");

    try {
      const response = await api.get(
        `/api/worksheets/${worksheetId}/questions/`
      );

      if (response.data) {
        const { worksheet, questions } = response.data;

        // Set worksheet parameters
        setSubject(worksheet.subject);
        setTopic(worksheet.topic || "");

        // Check if worksheet.difficulty exists in the current levels array
        // If not, we'll still set it but will rely on the levels useEffect to handle defaults
        setDifficulty(worksheet.difficulty || "");

        // Set My Questions inclusion flag if it exists in the worksheet data
        if (worksheet.include_my_questions !== undefined) {
          setIncludeMyQuestions(worksheet.include_my_questions);
        }

        // Try to parse question_sources if available
        let questionSources = {};
        try {
          if (worksheet.question_sources) {
            const sources = JSON.parse(worksheet.question_sources);
            questionSources = sources.reduce((acc, item) => {
              acc[item.reference_code] = item.source;
              return acc;
            }, {});
          }
        } catch (e) {
          console.error("Error parsing question sources:", e);
        }

        // Process questions with tempId for the UI
        const questionsWithId = questions.map((q, index) => ({
          ...q,
          tempId: `saved-${index}`, // Add temporary ID for tracking
          selected: true, // All questions start as selected
          // Apply source if available in question_sources, otherwise use default from question or "system"
          source: questionSources[q.reference_code] || q.source || "system",
        }));

        setGeneratedQuestions(questionsWithId);
        setSelectedQuestions(questionsWithId.map((q) => q.tempId));
        setShowLoadModal(false);

        // Pre-populate the save form with the worksheet info
        setWorksheetTitle(worksheet.title);
        setWorksheetDescription(worksheet.description || "");
      }
    } catch (error) {
      console.error("Error loading worksheet:", error);
      setError("Failed to load the worksheet. Please try again.");
    } finally {
      setIsLoadingQuestions(false);
    }
  };

  // Save current worksheet
  const saveWorksheet = async () => {
    if (!selectedQuestions.length) {
      toast.error(
        "Please select at least one question before saving the worksheet"
      );
      return;
    }

    if (!worksheetTitle.trim()) {
      toast.error("Please enter a title for your worksheet");
      return;
    }

    console.log("Current authenticated user:", auth.user);

    if (!auth.user || !auth.user.id) {
      toast.error("You must be logged in to save worksheets");
      return;
    }

    // DEBUGGING: Log the full authentication state
    console.log("Auth state:", auth);
    console.log("Local storage token:", localStorage.getItem("accessToken"));

    // Force a check of the current user before saving
    try {
      const userResponse = await api.get("/api/users/me/");
      console.log("Current user verification:", userResponse.data);

      if (userResponse.data.id !== auth.user.id) {
        console.log("User mismatch! Refreshing auth...");
        // Update auth context with latest user data
        auth.user = userResponse.data;
      }
    } catch (error) {
      console.error("Error verifying current user:", error);
      toast.error("Authentication issue. Please log in again.");
      navigate("/login");
      return;
    }

    // Get the actual question objects that are selected
    const selectedQuestionObjects = generatedQuestions.filter((q) =>
      selectedQuestions.includes(q.tempId)
    );

    // Extract reference_codes from the selected questions
    const question_codes = JSON.stringify(
      selectedQuestionObjects.map((q) => q.reference_code)
    );
    console.log("Saving question codes:", question_codes);

    const worksheetData = {
      title: worksheetTitle,
      description: worksheetDescription,
      subject: subject,
      topic: topic || "",
      question_codes,
      difficulty: difficulty,
      // Include information about whether My Questions were used
      include_my_questions: includeMyQuestions,
      // Add sources for each question to preserve the My Questions badge display
      question_sources: JSON.stringify(
        selectedQuestionObjects.map((q) => ({
          reference_code: q.reference_code,
          source: q.source || "system",
        }))
      ),
    };

    console.log("Saving worksheet with data:", worksheetData);
    setIsSavingWorksheet(true);

    try {
      const response = await api.post(
        "/api/worksheets/save_worksheet/",
        worksheetData
      );
      console.log("Save worksheet response:", response.data);

      // Store the worksheet ID for direct loading
      const worksheetId = response.data.id;

      toast.success("Worksheet saved successfully!");
      setShowSaveModal(false);
      // Reset form
      setWorksheetTitle("");
      setWorksheetDescription("");
      setIsSavingWorksheet(false);

      // Instead of immediately navigating to MyWorksheets, wait for a short delay
      // to ensure the server has processed the save
      setTimeout(() => {
        // Navigate to My Worksheets page to see the saved worksheet
        console.log("Redirecting to My Worksheets page...");
        navigate("/my-worksheets", {
          state: {
            fromSave: true,
            timestamp: Date.now(),
            savedWorksheetId: worksheetId,
          },
          replace: true, // Use replace to ensure a full reload of MyWorksheets
        });
      }, 500);
    } catch (error) {
      console.error("Error saving worksheet:", error);
      if (error.response) {
        console.error(
          "Error response:",
          error.response.status,
          error.response.data
        );
      }
      setIsSavingWorksheet(false);
      toast.error("Failed to save worksheet. Please try again.");
    }
  };

  // Fetch topic suggestions based on user input
  const fetchTopicSuggestions = async (inputTopic) => {
    if (!subject || inputTopic.length < 2) {
      setTopicSuggestions([]);
      return;
    }

    try {
      const params = new URLSearchParams();
      params.append("subject", subject);
      params.append("topic", inputTopic);
      params.append("suggestions_only", "true");

      const response = await api.get(
        `/api/csv-data/topic_suggestions/?${params.toString()}`
      );

      if (response.data && Array.isArray(response.data)) {
        setTopicSuggestions(response.data);
        setShowTopicSuggestions(response.data.length > 0);
      }
    } catch (error) {
      console.error("Error fetching topic suggestions:", error);
      setTopicSuggestions([]);
    }
  };

  // Handle topic input change with debounce
  const handleTopicChange = (value) => {
    setTopic(value);

    // Clear any existing timeout
    if (window.topicSearchTimeout) {
      clearTimeout(window.topicSearchTimeout);
    }

    // Set a new timeout to fetch suggestions
    window.topicSearchTimeout = setTimeout(() => {
      if (value.length >= 2 && subject) {
        fetchTopicSuggestions(value);
      } else {
        setTopicSuggestions([]);
        setShowTopicSuggestions(false);
      }
    }, 300); // 300ms debounce
  };

  // Select a topic suggestion
  const selectTopicSuggestion = (suggestion) => {
    setTopic(suggestion);
    setShowTopicSuggestions(false);
  };

  // Generate initial worksheet
  const generateWorksheet = async () => {
    // Validation
    if (!subject) {
      setError("Please select a subject");
      return;
    }

    if (!difficulty) {
      setError("Please select a difficulty");
      return;
    }

    if (numQuestions <= 0 || numQuestions > 15) {
      setError("Number of questions must be between 1 and 15");
      return;
    }

    setIsGenerating(true);
    setError("");

    try {
      const params = new URLSearchParams();
      params.append("subject", subject);
      if (topic) params.append("topic", topic);
      params.append("difficulty", difficulty);
      params.append("limit", numQuestions);
      if (paper) {
        // Add paper parameter if selected
        params.append("paper", parseInt(paper, 10));
      }

      // Add parameter for including user's questions
      if (includeMyQuestions) {
        params.append("include_my_questions", "true");
      }

      const response = await api.get(
        `/api/csv-data/worksheet/?${params.toString()}`
      );

      if (response.data && Array.isArray(response.data)) {
        const questionsWithId = response.data.map((q, index) => ({
          ...q,
          tempId: `new-${index}`, // Add temporary ID for tracking
          selected: true, // All questions start as selected
        }));

        setGeneratedQuestions(questionsWithId);
        setSelectedQuestions(questionsWithId.map((q) => q.tempId));
      } else {
        setError(
          "No questions found with the given criteria. Try different parameters."
        );
        setGeneratedQuestions([]);
        setSelectedQuestions([]);
      }
    } catch (error) {
      console.error("Error generating worksheet:", error);
      setError("Failed to generate worksheet. Please try again.");
    } finally {
      setIsGenerating(false);
    }
  };

  // Handle question selection/deselection
  const toggleQuestionSelection = (tempId) => {
    if (selectedQuestions.includes(tempId)) {
      // Remove from selected questions
      setSelectedQuestions((prev) => prev.filter((id) => id !== tempId));
    } else {
      // Add to selected questions
      setSelectedQuestions((prev) => [...prev, tempId]);
    }
  };

  // Regenerate unselected questions
  const regenerateQuestions = async () => {
    // Find which questions are unselected
    const unselectedQuestions = generatedQuestions.filter(
      (q) => !selectedQuestions.includes(q.tempId)
    );

    if (unselectedQuestions.length === 0) {
      setError("Please unselect at least one question to regenerate");
      return;
    }

    if (!regenerateDifficulty) {
      setError("Please select a difficulty for regeneration");
      return;
    }

    setIsRegenerating(true);
    setError("");

    try {
      const params = new URLSearchParams();
      params.append("subject", subject); // Subject remains locked
      if (regenerateTopic) params.append("topic", regenerateTopic);
      params.append("difficulty", regenerateDifficulty);
      params.append("limit", unselectedQuestions.length);
      if (regeneratePaper) {
        // Use regeneratePaper if set, otherwise fall back to the original paper
        params.append("paper", parseInt(regeneratePaper, 10));
      } else if (paper) {
        // Use original paper as fallback
        params.append("paper", parseInt(paper, 10));
      }

      // Add parameter for including user's questions
      if (includeMyQuestions) {
        params.append("include_my_questions", "true");
      }

      const response = await api.get(
        `/api/csv-data/worksheet/?${params.toString()}`
      );

      if (response.data && Array.isArray(response.data)) {
        // Create new questions with temporary IDs
        const newQuestions = response.data.map((q, index) => ({
          ...q,
          tempId: `regen-${Date.now()}-${index}`, // Unique ID for tracking
          selected: true, // All regenerated questions start as selected
        }));

        // Keep selected questions and replace unselected with new ones
        const updatedQuestions = [
          ...generatedQuestions.filter((q) =>
            selectedQuestions.includes(q.tempId)
          ),
          ...newQuestions,
        ];

        setGeneratedQuestions(updatedQuestions);
        // Update selected questions to include all the newly generated ones
        setSelectedQuestions([
          ...selectedQuestions,
          ...newQuestions.map((q) => q.tempId),
        ]);
      } else {
        setError(
          "No questions found with the given criteria for regeneration. Try different parameters."
        );
      }
    } catch (error) {
      console.error("Error regenerating questions:", error);
      setError("Failed to regenerate questions. Please try again.");
    } finally {
      setIsRegenerating(false);
    }
  };

  // Direct browser print implementation
  const directPrint = () => {
    setPrintStatus("printing");
    setShowPrintStatus(true);

    // Create a hidden iframe for printing
    const printFrame = document.createElement("iframe");
    printFrame.style.position = "fixed";
    printFrame.style.right = "0";
    printFrame.style.bottom = "0";
    printFrame.style.width = "0";
    printFrame.style.height = "0";
    printFrame.style.border = "0";
    document.body.appendChild(printFrame);

    // Get selected questions content
    const selectedQuestionsContent = generatedQuestions
      .filter((q) => selectedQuestions.includes(q.tempId))
      .map(
        (question, index) => `
        <div style="margin-bottom: 40px; page-break-after: always;">
          <h4>Question ${index + 1} ${
          question.source === "my_questions"
            ? '<span style="display: inline-block; background-color: #0dcaf0; color: white; font-size: 0.75rem; padding: 0.25rem 0.5rem; border-radius: 0.5rem; margin-left: 0.5rem;">My Question</span>'
            : ""
        }</h4>
          <div>${question.question_body || ""}</div>
        </div>
      `
      )
      .join("");

    // Get answers content
    const answersContent = generatedQuestions
      .filter((q) => selectedQuestions.includes(q.tempId))
      .map(
        (question, index) => `
        <div style="margin-bottom: 30px;">
          <h4>Answer ${index + 1} ${
          question.source === "my_questions"
            ? '<span style="display: inline-block; background-color: #0dcaf0; color: white; font-size: 0.75rem; padding: 0.25rem 0.5rem; border-radius: 0.5rem; margin-left: 0.5rem;">My Question</span>'
            : ""
        }</h4>
          <div>${question.markscheme_body || ""}</div>
        </div>
      `
      )
      .join("");

    // Create the full HTML document
    const printContent = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>Worksheet: ${subject} ${topic ? `- ${topic}` : ""}</title>
          <style>
            body { 
              font-family: 'Times New Roman', Times, serif; 
              line-height: 1.6;
              margin: 20px;
            }
            .page-break { page-break-before: always; }
            table { border-collapse: collapse; width: 100%; margin-bottom: 1rem; }
            table, th, td { border: 1px solid #ddd; padding: 8px; }
            img { max-width: 100%; height: auto; }
          </style>
        </head>
        <body>
          <div style="text-align: center; margin-bottom: 30px;">
            <h2>Worksheet: ${subject} ${topic ? `- ${topic}` : ""}</h2>
            <p>Difficulty: ${difficulty}${paper ? `, Paper ${paper}` : ""}</p>
          </div>
          
          ${selectedQuestionsContent}
          
          <div class="page-break"></div>
          
          <div style="margin-top: 50px;">
            <h3 style="text-align: center; margin-bottom: 30px;">Answer Key</h3>
            ${answersContent}
          </div>
        </body>
      </html>
    `;

    try {
      // Write the content to the iframe
      const frameDoc = printFrame.contentWindow.document;
      frameDoc.open();
      frameDoc.write(printContent);
      frameDoc.close();

      // Wait for all resources to load then print
      printFrame.onload = () => {
        try {
          printFrame.contentWindow.focus();
          printFrame.contentWindow.print();
          setPrintStatus("success");
          setTimeout(() => setShowPrintStatus(false), 3000);
        } catch (err) {
          console.error("Print window error:", err);
          setPrintStatus("error");
          setPrintError(
            "Error when printing. Try using keyboard shortcut Ctrl+P or Cmd+P instead."
          );
        }
      };

      // Add a cleanup function to remove the frame
      setTimeout(() => {
        try {
          document.body.removeChild(printFrame);
        } catch (e) {
          console.log("Frame already removed");
        }
      }, 5000);
    } catch (error) {
      console.error("Error in direct print:", error);
      setPrintStatus("error");
      setPrintError("Failed to prepare print document. Please try again.");
    }
  };

  // Prepare for preview and print
  const prepareForPreview = () => {
    if (selectedQuestions.length === 0) {
      setError("Please select at least one question for your worksheet");
      return;
    }

    setShowPreview(true);
  };

  // Function to safely render HTML content
  const renderHTML = (htmlContent) => {
    return { __html: htmlContent || "" };
  };

  // Download as HTML file
  const downloadAsHTML = () => {
    try {
      setPrintStatus("preparing");
      setShowPrintStatus(true);

      // Get selected questions content
      const selectedQuestionsContent = generatedQuestions
        .filter((q) => selectedQuestions.includes(q.tempId))
        .map(
          (question, index) => `
          <div style="margin-bottom: 40px; page-break-after: always;">
            <h4>Question ${index + 1} ${
            question.source === "my_questions"
              ? '<span style="display: inline-block; background-color: #0dcaf0; color: white; font-size: 0.75rem; padding: 0.25rem 0.5rem; border-radius: 0.5rem; margin-left: 0.5rem;">My Question</span>'
              : ""
          }</h4>
            <div>${question.question_body || ""}</div>
          </div>
        `
        )
        .join("");

      // Get answers content
      const answersContent = generatedQuestions
        .filter((q) => selectedQuestions.includes(q.tempId))
        .map(
          (question, index) => `
          <div style="margin-bottom: 30px;">
            <h4>Answer ${index + 1} ${
            question.source === "my_questions"
              ? '<span style="display: inline-block; background-color: #0dcaf0; color: white; font-size: 0.75rem; padding: 0.25rem 0.5rem; border-radius: 0.5rem; margin-left: 0.5rem;">My Question</span>'
              : ""
          }</h4>
            <div>${question.markscheme_body || ""}</div>
          </div>
        `
        )
        .join("");

      // Create the full HTML document
      const htmlContent = `
        <!DOCTYPE html>
        <html>
          <head>
            <title>Worksheet: ${subject} ${topic ? `- ${topic}` : ""}</title>
            <style>
              body { 
                font-family: 'Times New Roman', Times, serif; 
                line-height: 1.6;
                margin: 20px;
              }
              .page-break { page-break-before: always; }
              table { border-collapse: collapse; width: 100%; margin-bottom: 1rem; }
              table, th, td { border: 1px solid #ddd; padding: 8px; }
              img { max-width: 100%; height: auto; }
              @media print {
                .page-break { page-break-before: always; }
              }
            </style>
          </head>
          <body>
            <div style="text-align: center; margin-bottom: 30px;">
              <h2>Worksheet: ${subject} ${topic ? `- ${topic}` : ""}</h2>
              <p>Difficulty: ${difficulty}${paper ? `, Paper ${paper}` : ""}</p>
            </div>
            
            ${selectedQuestionsContent}
            
            <div class="page-break"></div>
            
            <div style="margin-top: 50px;">
              <h3 style="text-align: center; margin-bottom: 30px;">Answer Key</h3>
              ${answersContent}
            </div>
          </body>
        </html>
      `;

      // Create a blob and download link
      const blob = new Blob([htmlContent], { type: "text/html" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `Worksheet_${subject}_${topic || "Mixed"}.html`;
      document.body.appendChild(link);
      link.click();

      // Cleanup
      setTimeout(() => {
        URL.revokeObjectURL(url);
        document.body.removeChild(link);
        setPrintStatus("success");
        setTimeout(() => setShowPrintStatus(false), 3000);
      }, 100);
    } catch (error) {
      console.error("Error creating HTML download:", error);
      setPrintStatus("error");
      setPrintError(
        "Failed to create downloadable worksheet. Please try again."
      );
    }
  };

  // New function to handle loading questions from MyQuestions
  const loadQuestionsFromMyQuestions = (questions) => {
    console.log("Loading questions from MyQuestions:", questions);
    setIsLoadingQuestions(true);
    setError("");

    try {
      if (!Array.isArray(questions) || questions.length === 0) {
        console.error("No valid questions provided from MyQuestions");
        setError("No valid questions were provided. Please try again.");
        return;
      }

      // Process questions with tempId for the UI
      const questionsWithId = questions.map((q, index) => ({
        ...q,
        tempId: `myq-${index}`, // Add temporary ID for tracking
        selected: true, // All questions start as selected
        source: q.source || "user", // Mark as user source
      }));

      // Extract subject from the first question if available
      if (questionsWithId.length > 0 && questionsWithId[0].subject) {
        setSubject(questionsWithId[0].subject);
      }

      // Set worksheet to include my questions
      setIncludeMyQuestions(true);

      // Pre-populate the worksheet title based on questions
      const defaultTitle = `My Questions Worksheet (${new Date().toLocaleDateString()})`;
      setWorksheetTitle(defaultTitle);

      // Set the questions in the worksheet
      setGeneratedQuestions(questionsWithId);
      setSelectedQuestions(questionsWithId.map((q) => q.tempId));

      // Show a success message
      toast.success(
        `${questionsWithId.length} questions loaded from My Questions`
      );
    } catch (error) {
      console.error("Error processing selected questions:", error);
      setError("Failed to process the selected questions. Please try again.");
    } finally {
      setIsLoadingQuestions(false);
    }
  };

  return (
    <Container className="my-4">
      <Toaster position="top-right" />
      <h2>Worksheet Generator</h2>

      {error && !location.state?.isPublic && (
        <Alert variant="danger" onClose={() => setError("")} dismissible>
          {error}
        </Alert>
      )}

      {/* Print Status Toast */}
      <ToastContainer position="top-end" className="p-3">
        <Toast
          show={showPrintStatus}
          onClose={() => setShowPrintStatus(false)}
          bg={
            printStatus === "error"
              ? "danger"
              : printStatus === "success"
              ? "success"
              : "info"
          }
          delay={printStatus === "success" ? 3000 : undefined}
          autohide={printStatus === "success"}
        >
          <Toast.Header>
            <strong className="me-auto">
              {printStatus === "preparing" && "Preparing Worksheet"}
              {printStatus === "printing" && "Printing Worksheet"}
              {printStatus === "success" && "Action Successful"}
              {printStatus === "error" && "Error"}
            </strong>
          </Toast.Header>
          <Toast.Body className={printStatus === "error" ? "text-white" : ""}>
            {printStatus === "preparing" && (
              <div className="d-flex align-items-center">
                <Spinner animation="border" size="sm" className="me-2" />
                <span>Preparing your worksheet...</span>
              </div>
            )}
            {printStatus === "printing" && (
              <div className="d-flex align-items-center">
                <Spinner animation="border" size="sm" className="me-2" />
                <span>Sending to printer...</span>
              </div>
            )}
            {printStatus === "success" && "Your worksheet is ready!"}
            {printStatus === "error" && (
              <>
                <div>
                  {printError ||
                    "An error occurred while processing. Please try another option."}
                </div>
              </>
            )}
          </Toast.Body>
        </Toast>
      </ToastContainer>

      {!generatedQuestions.length ? (
        <Card className="mb-4">
          <Card.Header>
            <div className="d-flex justify-content-between align-items-center">
              <h4>Step 1: Initial Selection</h4>
              {savedWorksheets.length > 0 && (
                <Button
                  variant="outline-primary"
                  onClick={() => setShowLoadModal(true)}
                >
                  Load Saved Worksheet
                </Button>
              )}
            </div>
          </Card.Header>
          <Card.Body>
            <Form>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Subject</Form.Label>
                    <Form.Select
                      value={subject}
                      onChange={(e) => setSubject(e.target.value)}
                    >
                      <option value="">Select Subject</option>
                      {subjects.map((sub, index) => (
                        <option key={index} value={sub}>
                          {sub}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>

                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Topic (optional)</Form.Label>
                    <div className="position-relative">
                      <Form.Control
                        type="text"
                        placeholder="Enter topic (e.g., Algebra, Geometry)"
                        value={topic}
                        onChange={(e) => handleTopicChange(e.target.value)}
                        onFocus={() =>
                          setShowTopicSuggestions(topicSuggestions.length > 0)
                        }
                        onBlur={() =>
                          setTimeout(() => setShowTopicSuggestions(false), 200)
                        }
                      />
                      {showTopicSuggestions && topicSuggestions.length > 0 && (
                        <div
                          className="position-absolute w-100 mt-1 bg-white border rounded shadow-sm"
                          style={{
                            zIndex: 1000,
                            maxHeight: "200px",
                            overflowY: "auto",
                          }}
                        >
                          {topicSuggestions.map((suggestion, index) => (
                            <div
                              key={index}
                              className="px-3 py-2 suggestion-item"
                              style={{ cursor: "pointer" }}
                              onMouseDown={() =>
                                selectTopicSuggestion(suggestion)
                              }
                            >
                              {suggestion}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Difficulty</Form.Label>
                    <Form.Select
                      value={difficulty}
                      onChange={(e) => setDifficulty(e.target.value)}
                    >
                      {levels.length > 0 ? (
                        levels.map((level, index) => (
                          <option key={index} value={level}>
                            {level}
                          </option>
                        ))
                      ) : (
                        // Fallback options if levels haven't loaded yet
                        <>
                          <option value="Basic">Basic</option>
                          <option value="Intermediate">Intermediate</option>
                          <option value="Hard">Hard</option>
                        </>
                      )}
                    </Form.Select>
                  </Form.Group>
                </Col>

                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Number of Questions (Max 15)</Form.Label>
                    <Form.Control
                      type="number"
                      min="1"
                      max="15"
                      value={numQuestions}
                      onChange={(e) =>
                        setNumQuestions(parseInt(e.target.value, 10))
                      }
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Paper (Optional)</Form.Label>
                    <Form.Select
                      value={paper}
                      onChange={(e) => setPaper(e.target.value)}
                    >
                      <option value="">Any Paper</option>
                      <option value="1">Paper 1</option>
                      <option value="2">Paper 2</option>
                      <option value="3">Paper 3</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3 mt-4">
                    <Form.Check
                      type="checkbox"
                      id="include-my-questions"
                      label="Include questions from My Questions"
                      checked={includeMyQuestions}
                      onChange={(e) => setIncludeMyQuestions(e.target.checked)}
                    />
                  </Form.Group>
                </Col>
              </Row>

              <div className="d-grid gap-2">
                <Button
                  variant="primary"
                  onClick={generateWorksheet}
                  disabled={isGenerating}
                >
                  {isGenerating ? (
                    <>
                      <Spinner
                        as="span"
                        animation="border"
                        size="sm"
                        role="status"
                        aria-hidden="true"
                      />
                      <span className="ms-2">Generating...</span>
                    </>
                  ) : (
                    "Generate Worksheet"
                  )}
                </Button>
              </div>
            </Form>
          </Card.Body>
        </Card>
      ) : (
        <>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h4>
              Subject: {subject} {topic && `- Topic: ${topic}`}
            </h4>
            <div>
              <Button
                variant="outline-success"
                onClick={() => setShowSaveModal(true)}
                className="me-2"
                disabled={isViewOnly}
                title={
                  isViewOnly
                    ? "You cannot save changes to shared worksheets from other users"
                    : "Save Worksheet"
                }
              >
                Save Worksheet
              </Button>
              {savedWorksheets.length > 0 && (
                <Button
                  variant="outline-primary"
                  onClick={() => setShowLoadModal(true)}
                >
                  Load Saved Worksheet
                </Button>
              )}
            </div>
          </div>

          <Card className="mb-4">
            <Card.Header>
              <h4>Step 2: Select/Unselect Questions</h4>
            </Card.Header>
            <Card.Body>
              <ListGroup>
                {generatedQuestions.map((question, index) => (
                  <ListGroup.Item key={question.tempId} className="mb-2">
                    <Form.Check
                      type="checkbox"
                      id={`question-${question.tempId}`}
                      label={`Question ${index + 1}`}
                      checked={selectedQuestions.includes(question.tempId)}
                      onChange={() => toggleQuestionSelection(question.tempId)}
                      className="mb-2"
                    />

                    <div className="ms-4">
                      <h5>
                        {question.title}
                        {question.source === "my_questions" && (
                          <Badge bg="info" pill className="ms-2">
                            My Question
                          </Badge>
                        )}
                      </h5>
                      <div
                        className="question-preview html-content-view"
                        dangerouslySetInnerHTML={renderHTML(
                          question.question_body
                        )}
                      />
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        className="mt-2"
                        onClick={() => {
                          // Toggle visibility of answer
                          const elem = document.getElementById(
                            `answer-${question.tempId}`
                          );
                          if (elem) {
                            elem.style.display =
                              elem.style.display === "none" ? "block" : "none";
                          }
                        }}
                      >
                        Toggle Answer
                      </Button>
                      <div
                        id={`answer-${question.tempId}`}
                        className="answer-preview mt-2 html-content-view"
                        style={{ display: "none" }}
                        dangerouslySetInnerHTML={renderHTML(
                          question.markscheme_body
                        )}
                      />
                    </div>
                  </ListGroup.Item>
                ))}
              </ListGroup>

              <div className="d-flex justify-content-between mt-4">
                <div>
                  <Button
                    variant="danger"
                    onClick={() => {
                      setGeneratedQuestions([]);
                      setSelectedQuestions([]);
                      setTopic(""); // Reset for new worksheet
                      setRegenerateTopic("");
                    }}
                  >
                    Start Over
                  </Button>
                </div>
                <div>
                  <Button
                    variant="success"
                    onClick={prepareForPreview}
                    disabled={selectedQuestions.length === 0}
                    className="ms-2"
                  >
                    Preview & Options
                  </Button>
                </div>
              </div>
            </Card.Body>
          </Card>

          <Card className="mb-4">
            <Card.Header>
              <h4>Step 3: Regenerate Unselected Questions</h4>
            </Card.Header>
            <Card.Body>
              <Form>
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>New Topic (optional)</Form.Label>
                      <div className="position-relative">
                        <Form.Control
                          type="text"
                          placeholder="Enter new topic"
                          value={regenerateTopic}
                          onChange={(e) => {
                            setRegenerateTopic(e.target.value);
                            handleTopicChange(e.target.value);
                          }}
                          onFocus={() =>
                            setShowTopicSuggestions(topicSuggestions.length > 0)
                          }
                          onBlur={() =>
                            setTimeout(
                              () => setShowTopicSuggestions(false),
                              200
                            )
                          }
                        />
                        {showTopicSuggestions &&
                          topicSuggestions.length > 0 && (
                            <div
                              className="position-absolute w-100 mt-1 bg-white border rounded shadow-sm"
                              style={{
                                zIndex: 1000,
                                maxHeight: "200px",
                                overflowY: "auto",
                              }}
                            >
                              {topicSuggestions.map((suggestion, index) => (
                                <div
                                  key={index}
                                  className="px-3 py-2 suggestion-item"
                                  style={{ cursor: "pointer" }}
                                  onMouseDown={() => {
                                    selectTopicSuggestion(suggestion);
                                    setRegenerateTopic(suggestion);
                                  }}
                                >
                                  {suggestion}
                                </div>
                              ))}
                            </div>
                          )}
                      </div>
                    </Form.Group>
                  </Col>

                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>New Difficulty</Form.Label>
                      <Form.Select
                        value={regenerateDifficulty}
                        onChange={(e) =>
                          setRegenerateDifficulty(e.target.value)
                        }
                      >
                        {levels.length > 0 ? (
                          levels.map((level, index) => (
                            <option key={index} value={level}>
                              {level}
                            </option>
                          ))
                        ) : (
                          // Fallback options if levels haven't loaded yet
                          <>
                            <option value="Basic">Basic</option>
                            <option value="Intermediate">Intermediate</option>
                            <option value="Hard">Hard</option>
                          </>
                        )}
                      </Form.Select>
                    </Form.Group>
                  </Col>
                </Row>

                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>New Paper (Optional)</Form.Label>
                      <Form.Select
                        value={regeneratePaper}
                        onChange={(e) => setRegeneratePaper(e.target.value)}
                      >
                        <option value="">Any Paper</option>
                        <option value="1">Paper 1</option>
                        <option value="2">Paper 2</option>
                        <option value="3">Paper 3</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>
                </Row>

                <div className="d-grid gap-2">
                  <Button
                    variant="primary"
                    onClick={regenerateQuestions}
                    disabled={
                      isRegenerating ||
                      selectedQuestions.length === generatedQuestions.length
                    }
                  >
                    {isRegenerating ? (
                      <>
                        <Spinner
                          as="span"
                          animation="border"
                          size="sm"
                          role="status"
                          aria-hidden="true"
                        />
                        <span className="ms-2">Regenerating...</span>
                      </>
                    ) : (
                      `Regenerate ${
                        generatedQuestions.length - selectedQuestions.length
                      } Unselected Questions`
                    )}
                  </Button>
                </div>
              </Form>
            </Card.Body>
          </Card>
        </>
      )}

      {/* Preview Modal */}
      <Modal
        show={showPreview}
        onHide={() => setShowPreview(false)}
        size="xl"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Worksheet Preview</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div ref={printRef} className="p-4">
            <div className="text-center mb-4">
              <h2>
                Worksheet: {subject} {topic && `- ${topic}`}
              </h2>
              <p>
                Difficulty: {difficulty}
                {paper ? `, Paper ${paper}` : ""}
              </p>
            </div>

            {generatedQuestions
              .filter((q) => selectedQuestions.includes(q.tempId))
              .map((question, index) => (
                <div key={question.tempId} className="mb-5">
                  <h4>
                    Question {index + 1}
                    {question.source === "my_questions" && (
                      <Badge bg="info" pill className="ms-2">
                        My Question
                      </Badge>
                    )}
                  </h4>
                  <div
                    className="question-text html-content-view mb-3"
                    dangerouslySetInnerHTML={renderHTML(question.question_body)}
                  />
                  <div className="page-break-after"></div>
                </div>
              ))}

            {/* Answer section - will only show during print */}
            <div className="mt-5 answer-section">
              <h3 className="text-center mb-4">Answer Key</h3>
              {generatedQuestions
                .filter((q) => selectedQuestions.includes(q.tempId))
                .map((question, index) => (
                  <div key={`answer-${question.tempId}`} className="mb-4">
                    <h4>
                      Answer {index + 1}
                      {question.source === "my_questions" && (
                        <Badge bg="info" pill className="ms-2">
                          My Question
                        </Badge>
                      )}
                    </h4>
                    <div
                      className="answer-text html-content-view"
                      dangerouslySetInnerHTML={renderHTML(
                        question.markscheme_body
                      )}
                    />
                  </div>
                ))}
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowPreview(false)}>
            Close
          </Button>
          <div className="ms-auto d-flex gap-2">
            <SmartPDFButton
              selectedQuestions={generatedQuestions.filter((q) =>
                selectedQuestions.includes(q.tempId)
              )}
              worksheetInfo={{
                title: worksheetTitle || `${subject} ${topic || ""} Worksheet`,
                subject,
                topic,
                difficulty,
                paper,
              }}
              settings={smartPDFSettings}
              size="sm"
            />

            <Button
              variant="outline-secondary"
              size="sm"
              onClick={() => setShowSettingsModal(true)}
              title="Smart PDF Settings"
            >
              <FaCog />
            </Button>

            <Button
              variant="outline-primary"
              onClick={directPrint}
              size="sm"
              disabled={
                printStatus === "preparing" || printStatus === "printing"
              }
              className="d-flex align-items-center gap-1"
            >
              {printStatus === "preparing" || printStatus === "printing" ? (
                <>
                  <Spinner
                    as="span"
                    animation="border"
                    size="sm"
                    role="status"
                    aria-hidden="true"
                  />
                  <span>
                    {printStatus === "preparing"
                      ? "Preparing..."
                      : "Printing..."}
                  </span>
                </>
              ) : (
                <>
                  <FaPrint />
                  Standard Print
                </>
              )}
            </Button>

            <Button
              variant="outline-primary"
              onClick={downloadAsHTML}
              style={{ display: "none" }}
            >
              Download as HTML
            </Button>
          </div>
        </Modal.Footer>
      </Modal>

      {/* Save Worksheet Modal */}
      <Modal
        show={showSaveModal}
        onHide={() => setShowSaveModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Save Worksheet</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Title</Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter worksheet title"
                value={worksheetTitle}
                onChange={(e) => setWorksheetTitle(e.target.value)}
                required
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Description (optional)</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                placeholder="Enter description"
                value={worksheetDescription}
                onChange={(e) => setWorksheetDescription(e.target.value)}
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowSaveModal(false)}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={saveWorksheet}
            disabled={isSavingWorksheet || !worksheetTitle}
          >
            {isSavingWorksheet ? (
              <>
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  role="status"
                  aria-hidden="true"
                />
                <span className="ms-2">Saving...</span>
              </>
            ) : (
              "Save Worksheet"
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Load Worksheet Modal */}
      <Modal
        show={showLoadModal}
        onHide={() => setShowLoadModal(false)}
        centered
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>Load Saved Worksheet</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {isLoadingWorksheets ? (
            <div className="text-center py-5">
              <Spinner animation="border" variant="primary" />
              <p className="mt-3">Loading your saved worksheets...</p>
            </div>
          ) : savedWorksheets.length === 0 ? (
            <Alert variant="info">
              You don't have any saved worksheets yet.
            </Alert>
          ) : (
            <ListGroup>
              {savedWorksheets.map((worksheet) => (
                <ListGroup.Item
                  key={worksheet.id}
                  action
                  onClick={() => loadWorksheet(worksheet.id)}
                >
                  <div className="d-flex justify-content-between align-items-start">
                    <div>
                      <h5>{worksheet.title}</h5>
                      <p className="mb-1">
                        <Badge bg="primary" className="me-2">
                          {worksheet.subject}
                        </Badge>
                        {worksheet.topic && (
                          <Badge bg="secondary" className="me-2">
                            {worksheet.topic}
                          </Badge>
                        )}
                        <Badge bg="info">{worksheet.difficulty}</Badge>
                      </p>
                      {worksheet.description && (
                        <p className="text-muted small mb-0">
                          {worksheet.description}
                        </p>
                      )}
                    </div>
                    <small className="text-muted">
                      {new Date(worksheet.created_at).toLocaleDateString()}
                    </small>
                  </div>
                </ListGroup.Item>
              ))}
            </ListGroup>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowLoadModal(false)}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={() => fetchSavedWorksheets()}
            disabled={isLoadingWorksheets}
          >
            <i className="fas fa-sync-alt me-2"></i>
            Refresh
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Smart PDF Settings Modal */}
      <SmartPDFSettings
        show={showSettingsModal}
        onHide={() => setShowSettingsModal(false)}
        settings={smartPDFSettings}
        onChange={setSmartPDFSettings}
      />
    </Container>
  );
};

export default WorksheetGenerator;
